import React, { useContext, useState, useMemo } from 'react';
import { string, bool, func, number } from 'prop-types';

import InspectionContext from '../../../contexts/InspectionContext';
import { RolesContext } from '../../../contexts/RolesContext';
import AppDivisionContext from '../../../contexts/AppDivisionContext';
import DashboardContext from '../../../contexts/DashboardContext';
import { FormContext, FormContextProvider } from '@stormwater-software/lib-form';
import FormModal from '../../Shared/form/modal/FormModal';
import { FormSchemaFields } from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { Redirect } from 'react-router-dom';
import inspectionApi from '../../../utils/api/inspection';
import ActionButtons from '../../Shared/ActionButtons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loading from '../../Shared/ResourceIndex/Loading';
import { ProjectContext } from '../../../contexts/ProjectContext';
import TenantFeatureContext from '../../../contexts/TenantContext';
import WeatherWidget from '../../Dashboard/WeatherWidget';
import SuccessNotification from '../../Shared/SuccessNotification/SuccessNotification';
import ProjectPermissionContext from '../../../contexts/ProjectPermissionContext';
import { useOffline } from '../../../contexts/OfflineContext';

// Deletion Form Component
const InspectionDeletionForm = ({ onSubmit, onCancel }) => {
  const deletionSchema = useMemo(() => {
    return {
      reason: {
        controlType: UIControlType.textarea,
        rows: 4,
        validation: {
          required: true,
        },
      },
    };
  }, []);

  return (
    <FormContextProvider initialValue={{ reason: '' }}>
      <FormContext.Consumer>
        {formContext => {
          const isSubmitDisabled = !formContext.value.reason || !formContext.value.reason.trim();
          
          return (
            <FormModal
              modalProps={{
                title: 'Are you sure you want to cancel / delete this inspection?',
                submitBtnText: 'Delete',
              }}
              onCancel={onCancel}
              onSubmit={() => onSubmit(formContext.value)}
              submitDisabled={isSubmitDisabled}
              submitText='Delete'
            >
              <div style={{ marginBottom: '1rem' }}>
                <strong>The inspection will be hidden from all views. This action cannot be reverted.</strong>
              </div>
          
              <FormSchemaFields
                schema={deletionSchema}
                formData={formContext.value}
                onChange={formContext.set}
                children={<div style={{marginBottom:'1rem'}}><strong>Please provide a reason as to why this inspection is being canceled / deleted:</strong></div>}
              />
                            

            </FormModal>
          );
        }}
      </FormContext.Consumer>
    </FormContextProvider>
  );
};

function InspectionHeader({
  title,
  projectName,
  clientName,
  enablePrint,
  handlePrint,
  handleSiteMapPrint,
  inspectionNumber,
  inspectionId,
  customTab,
  handleAddToReport,
  showModal,
}) {
  const projectContext = useContext(ProjectContext);
  const inspectionStore = useContext(InspectionContext);
  const roleStore = useContext(RolesContext);
  const featureContext = useContext(TenantFeatureContext);
  const { fetchStatsFuncForID, getTeamManagementData } =
    useContext(DashboardContext);
  const { getPath, getPublicPath } = useContext(AppDivisionContext);
  const { isInspectionHeaderLoader } = useContext(ProjectContext);
  const userHasPermission = useContext(RolesContext).userHasPermission;
  const projectPermissionContext = useContext(ProjectPermissionContext);

  const [showDelete, setShowDelete] = useState(false);
  const [toProject, setToProject] = useState(false);
  const [showForecast, setShowForecast] = useState(false);

  const handleDeleteSubmit = (formData) => {
    inspectionApi.destroy(inspectionStore.inspectionId, formData.reason).then(() => {
      setToProject(true);
      fetchStatsFuncForID.refetch();
      getTeamManagementData.refetch();
    });
  };

  const isPublic = window.location.pathname.startsWith('/public');
  const isOffline = useOffline();

  // init actions

  const secondaryActions = [
    {
      label: 'Back to Project',
      icon: 'folder',
      onClick: () => setToProject(true),
    },
  ];

  if (roleStore.userHasPermission('delete', 'Inspection') && !projectContext.isDownloaded()) {
    secondaryActions.push({
      label: 'Delete Inspection',
      icon: 'trash',
      onClick: () => setShowDelete(true),
    });
  }

  /**
   * Print should be last
   *
   * @todo enable this button via a "Print Inspection" permission
   * if (roleStore.userHasPermission('all', 'Print Inspection')) { }
   * */
  if (enablePrint && roleStore.userHasPermission('all', 'Download')) {
    secondaryActions.push({
      label: 'Print Inspection',
      icon: 'print',
      onClick: handlePrint,
    });
  }

  if (featureContext.tenantHasFeature('Site Map - Editor Updates')) {
    secondaryActions.push({
      icon: 'print',
      label: 'Print Site Map',
      onClick: handleSiteMapPrint,
    });
  }

  if (window.innerWidth < 568) {
    secondaryActions.push({
      icon: 'cloud',
      label: 'Show Forecast',
      onClick: () => {
        setShowForecast(!showForecast);
        projectContext.fetchWeatherData();
      },
    });
  }

  // render
  if (toProject) {
    if (isPublic) {
      return customTab ? (
        <Redirect
          to={getPublicPath(
            `/projects/${inspectionStore.getProjectId()}/project-docs`,
          )}
          push
        />
      ) : (
        <Redirect
          to={getPublicPath(
            `/projects/${inspectionStore.getProjectId()}/inspections`,
          )}
          push
        />
      );
    }

    return customTab ? (
      <Redirect
        to={getPath(`/projects/${inspectionStore.getProjectId()}/project-docs`)}
        push
      />
    ) : (
      <Redirect
        to={getPath(`/projects/${inspectionStore.getProjectId()}/inspections`)}
        push
      />
    );
  }

  if (isInspectionHeaderLoader) {
    return <Loading what='details' />;
  }

  return (
    <>
      {showForecast && !isOffline && (
        <div className='forecast-min-width'>
          <div style={{ display: 'grid' }}>
            <WeatherWidget isProject={true} />
            {!projectPermissionContext.readOnly &&
              userHasPermission('create', 'Inspection Notes') && (
                <ActionButtons
                  primary='Add to Report'
                  className='small-button-add-to-report'
                  onClick={handleAddToReport}
                />
              )}
          </div>
        </div>
      )}

      <header className='inspection-header'>
        <div className='inspection-header-info text-truncate'>
          <h2 className='inspection-header__title'>
            {title.split('|')[0]} |
            {title && title.includes('|') && title.split('|')[1]
              ? title
                  .split('|')[1]
                  .split(',')
                  .map((item, index) => (
                    <span
                      key={index}
                      className='inspection-header__inspection-type'
                    >
                      {item.trim()}
                    </span>
                  ))
              : null}{' '}
          </h2>
          <h5>
            <FontAwesomeIcon icon='hashtag' className='header-icon' />
            {inspectionNumber} | ID {inspectionId}
          </h5>
          <h5>
            <FontAwesomeIcon icon='folder' className='header-icon' />
            {projectName}
          </h5>
          <h5>
            <FontAwesomeIcon icon='user' className='header-icon' />
            {clientName}
          </h5>
        </div>

        {!isOffline && (
          <div className='project-header-buttons'>
            <ActionButtons secondary={secondaryActions} />
            <div className='forecast-max-width'>
              {showForecast ? (
                <div style={{ display: 'grid' }}>
                  <WeatherWidget isProject={true} />
                  {!projectPermissionContext.readOnly &&
                    userHasPermission('create', 'Inspection Notes') && (
                      <ActionButtons
                        primary='Add to Report'
                        className='small-button-add-to-report'
                        onClick={handleAddToReport}
                      />
                    )}
                </div>
              ) : (
                <ActionButtons
                  primary='Show Forecast'
                  onClick={() => {
                    setShowForecast(!showForecast);
                    projectContext.fetchWeatherData();
                  }}
                />
              )}
            </div>
          </div>
        )}

        <SuccessNotification show={showModal === 'success'} />
        {showDelete && (
          <InspectionDeletionForm
            onSubmit={handleDeleteSubmit}
            onCancel={() => setShowDelete(false)}
          />
        )}
      </header>
    </>
  );
}

InspectionHeader.propTypes = {
  title: string.isRequired,
  enablePrint: bool,
  handlePrint: func,
  inspectionNumber: number,
  inspectionId: number,
  handleAddToReport: func.isRequired,
  showModal: string.isRequired,
};

InspectionHeader.defaultProps = {
  enablePrint: false,
  handlePrint: () => {},
  handleSiteMapPrint: () => {},
  inspNumber: 0,
  inspId: 0,
};

export default InspectionHeader;
